// "Find a filter…": matches typed words against each filter's name, the groups
// it sits in, and the options or presets it offers, so finding a filter doesn't
// take knowing where the data model keeps it.

import type { FilterInterface } from "./SearchFilters";
import { renderPath, toLabel } from "./filterLabels";
import { getRangePresets } from "./filterPresets";
import { leafKind, sortChildren, type LeafKind } from "./filterState";

export type FilterMatch = {
  filter: FilterInterface;
  /** Labels of the groups it sits in, outermost first. */
  groups: string[];
  /** Options or presets that matched words its name and groups didn't. */
  values: string[];
};

export type FilterSearchResult = {
  /** Best first, at most `limit` of them. */
  matches: FilterMatch[];
  /** How many filters matched in all. */
  total: number;
};

type Option = { label: string; text: string };

type IndexedFilter = {
  filter: FilterInterface;
  groups: string[];
  name: string;
  context: string;
  options: Option[];
};

export type FilterIndex = IndexedFilter[];

// Case, accents, underscores and spacing don't matter.
const normalize = (text: string): string =>
  text
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .toLowerCase()
    .replace(/[_\s]+/g, " ")
    .trim();

// Yes/No aren't searched: they'd match any query containing "no".
const optionLabels = (filter: FilterInterface, kind: LeafKind): string[] => {
  if (kind === "list") {
    if (filter.type === "boolean") return [];
    return (filter.allowed_values ?? []).map((value) => toLabel(value, false));
  }
  if (kind === "range") {
    return getRangePresets(filter.path).map((preset) => preset.label);
  }
  return [];
};

// Every leaf that renders an input, in the order the tree shows them.
export const buildFilterIndex = (schema: FilterInterface): FilterIndex => {
  const index: FilterIndex = [];
  const walk = (node: FilterInterface, groups: string[]) => {
    if (node.children) {
      // The root is the whole filter, not a group of its own.
      const inner =
        node === schema ? groups : [...groups, renderPath(node.path)];
      for (const child of sortChildren(node.children)) walk(child, inner);
      return;
    }
    const kind = leafKind(node);
    if (kind === null) return;
    const label = renderPath(node.path);
    const name = normalize(label);
    // A group named like its only filter adds nothing, e.g. "Latest funding".
    const shown = groups.filter((group) => normalize(group) !== name);
    index.push({
      filter: node,
      groups: shown,
      name,
      context: normalize([label, ...shown].join(" ")),
      options: optionLabels(node, kind).map((option) => ({
        label: option,
        text: normalize(option),
      })),
    });
  };
  walk(schema, []);
  return index;
};

// A filter matches when every word appears in its name, its groups or its
// options. Name matches rank first, then group matches, then option matches.
export const findFilters = (
  index: FilterIndex,
  query: string,
  limit: number,
): FilterSearchResult => {
  const phrase = normalize(query);
  const words = phrase.split(" ").filter(Boolean);
  if (words.length === 0) return { matches: [], total: 0 };

  const ranked: Array<{ rank: number; match: FilterMatch }> = [];
  for (const entry of index) {
    const rest = words.filter((word) => !entry.context.includes(word));
    let rank: number;
    let values: string[] = [];
    if (rest.length === 0) {
      if (entry.name.startsWith(phrase)) rank = 0;
      else if (words.every((word) => entry.name.includes(word))) rank = 1;
      else rank = 2;
    } else {
      // Options holding all the remaining words, else any option holding
      // one of them, as long as each word turns up in some option.
      const whole = entry.options.filter((option) =>
        rest.every((word) => option.text.includes(word)),
      );
      const hits = whole.length
        ? whole
        : entry.options.filter((option) =>
            rest.some((word) => option.text.includes(word)),
          );
      const covered = rest.every((word) =>
        hits.some((option) => option.text.includes(word)),
      );
      if (!covered) continue;
      rank = 3;
      values = hits.map((option) => option.label);
    }
    ranked.push({
      rank,
      match: { filter: entry.filter, groups: entry.groups, values },
    });
  }

  // A stable sort, so equal ranks keep the tree's order.
  ranked.sort((a, b) => a.rank - b.rank);
  return {
    matches: ranked.slice(0, limit).map(({ match }) => match),
    total: ranked.length,
  };
};
